import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    User,
    ArrowLeft,
    Building,
    Mail,
    Globe,
    XCircle,
    CheckCircle,
    AlertCircle,
    Loader2,
    Search,
    ChevronDown,
    Plus,
    Eye,
    EyeOff,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { adminService, AdminCreateUserData } from '@/services/adminService'
import { helpersService, CryptoType } from '@/services/helpersService'
import { Currency, Country } from '@/types'
import { useAuth } from '@/hooks/useAuth'
import { canManageUsers, getVisibleRoles } from '@/utils/auth'
import { useAuditLog } from '@/utils/auditLogger'

interface UserOption {
    id: number
    email: string
    full_name: string
    role: string
}

export default function AdminCreateUser() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const { user } = useAuth()
    const { logDataModification } = useAuditLog()
    const [loading, setLoading] = useState(false)
    const [currencies, setCurrencies] = useState<Currency[]>([])
    const [countries, setCountries] = useState<Country[]>([])
    const [cryptoTypes, setCryptoTypes] = useState<CryptoType[]>([])
    const [users, setUsers] = useState<UserOption[]>([])
    const [currencySearchQuery, setCurrencySearchQuery] = useState('')
    const [countrySearchQuery, setCountrySearchQuery] = useState('')
    const [cryptoTypeSearchQuery, setCryptoTypeSearchQuery] = useState('')
    const [userSearchQuery, setUserSearchQuery] = useState('')
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false)
    const [showCountryDropdown, setShowCountryDropdown] = useState(false)
    const [showCryptoTypeDropdown, setShowCryptoTypeDropdown] = useState(false)
    const [showUserDropdown, setShowUserDropdown] = useState(false)
    const [currencyLoading, setCurrencyLoading] = useState(false)
    const [countryLoading, setCountryLoading] = useState(false)
    const [cryptoTypeLoading, setCryptoTypeLoading] = useState(false)
    const [userLoading, setUserLoading] = useState(false)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [showTemporaryPassword, setShowTemporaryPassword] = useState(false)
    const [createdUser, setCreatedUser] = useState<any>(null)
    const [temporaryPassword, setTemporaryPassword] = useState('')

    const currencyDropdownRef = useRef<HTMLDivElement>(null)
    const countryDropdownRef = useRef<HTMLDivElement>(null)
    const cryptoTypeDropdownRef = useRef<HTMLDivElement>(null)
    const userDropdownRef = useRef<HTMLDivElement>(null)

    const [formData, setFormData] = useState<AdminCreateUserData>({
        email: '',
        first_name: '',
        last_name: '',
        role: 'merchant_admin',
        company_name: '',
        phone_number: '',
        country: undefined,
        merchant_id: undefined,
        submerchant_id: undefined,
        send_welcome_email: true,
        create_merchant: false,
        merchant_name: '',
        merchant_email: '',
        merchant_description: '',
        merchant_website_url: '',
        merchant_currency_id: undefined,
        merchant_country_id: undefined,
        merchant_success_redirect_url: '',
        merchant_cancel_redirect_url: '',
        merchant_webhook_url: '',
        merchant_commission_rate: 0.00,
        // Payout configuration fields
        merchant_payout_crypto_enabled: false,
        merchant_payout_wallet_address: '',
        merchant_payout_crypto_type_id: undefined,
    })

    useEffect(() => {
        fetchCurrencies()
        fetchCountries()
        fetchCryptoTypes()
        fetchUsers()
        // set default role for formData
        setFormData(prev => ({
            ...prev,
            role: canManageUsers(user) ? 'merchant_admin' : 'merchant_user',
        }))
    }, [user])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                currencyDropdownRef.current &&
                !currencyDropdownRef.current.contains(event.target as Node)
            ) {
                setShowCurrencyDropdown(false)
            }
            if (
                countryDropdownRef.current &&
                !countryDropdownRef.current.contains(event.target as Node)
            ) {
                setShowCountryDropdown(false)
            }
            if (
                cryptoTypeDropdownRef.current &&
                !cryptoTypeDropdownRef.current.contains(event.target as Node)
            ) {
                setShowCryptoTypeDropdown(false)
            }
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
                setShowUserDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const fetchCurrencies = async () => {
        try {
            const data = await helpersService.getCurrencies()
            setCurrencies(data)
        } catch (error) {
            console.error('Failed to fetch currencies:', error)
        }
    }


    const fetchCountries = async () => {
        try {
            const data = await helpersService.getCountries()
            setCountries(data)
        } catch (error) {
            console.error('Failed to fetch countries:', error)
        }
    }

    const fetchCryptoTypes = async () => {
        try {
            const data = await helpersService.getActiveCryptoTypes()
            setCryptoTypes(data)
        } catch (error) {
            console.error('Failed to fetch crypto types:', error)
        }
    }

    const fetchUsers = async () => {
        try {
            const result = await adminService.getUsers()
            if (result.success && result.data) {
                setUsers(result.data.results)
            } else {
                console.error('Failed to fetch users:', result)
            }
        } catch (error) {
            console.error('Failed to fetch users:', error)
        }
    }

    const searchCurrencies = async (query: string) => {
        if (!query.trim()) {
            setCurrencies([])
            return
        }

        setCurrencyLoading(true)
        try {
            const data = await helpersService.searchCurrencies(query)
            setCurrencies(data)
        } catch (error) {
            console.error('Failed to search currencies:', error)
        } finally {
            setCurrencyLoading(false)
        }
    }

    const searchCountries = async (query: string) => {
        if (!query.trim()) {
            setCountries([])
            return
        }

        setCountryLoading(true)
        try {
            const data = await helpersService.searchCountries(query)
            setCountries(data)
        } catch (error) {
            console.error('Failed to search countries:', error)
        } finally {
            setCountryLoading(false)
        }
    }

    const searchCryptoTypes = async (query: string) => {
        if (!query.trim()) {
            setCryptoTypes([])
            return
        }

        setCryptoTypeLoading(true)
        try {
            const data = await helpersService.searchCryptoTypes(query)
            setCryptoTypes(data)
        } catch (error) {
            console.error('Failed to search crypto types:', error)
        } finally {
            setCryptoTypeLoading(false)
        }
    }

    const searchUsers = async (query: string) => {
        if (!query.trim()) {
            fetchUsers()
            return
        }

        setUserLoading(true)
        try {
            const result = await adminService.searchUsers(query)
            if (result.success && result.data) {
                setUsers(result.data.results)
            } else {
                console.error('Failed to search users:', result)
            }
        } catch (error) {
            console.error('Failed to search users:', error)
        } finally {
            setUserLoading(false)
        }
    }

    const handleCurrencySearch = (query: string) => {
        setCurrencySearchQuery(query)
        if (query.length >= 2) {
            searchCurrencies(query)
        } else if (query.length === 0) {
            fetchCurrencies()
        }
    }

    const handleCountrySearch = (query: string) => {
        setCountrySearchQuery(query)
        if (query.length >= 2) {
            searchCountries(query)
        } else if (query.length === 0) {
            fetchCountries()
        }
    }

    const handleCryptoTypeSearch = (query: string) => {
        setCryptoTypeSearchQuery(query)
        if (query.length >= 2) {
            searchCryptoTypes(query)
        } else if (query.length === 0) {
            fetchCryptoTypes()
        }
    }

    const handleUserSearch = (query: string) => {
        setUserSearchQuery(query)
        if (query.length >= 2) {
            searchUsers(query)
        } else if (query.length === 0) {
            fetchUsers()
        }
    }

    const selectCurrency = (currency: Currency) => {
        setFormData(prev => ({
            ...prev,
            merchant_currency_id: currency.id,
        }))
        setCurrencySearchQuery(`${currency.code} - ${currency.name}`)
        setShowCurrencyDropdown(false)
    }

    const selectCountry = (country: Country) => {
        setFormData(prev => ({
            ...prev,
            merchant_country_id: country.id,
        }))
        setCountrySearchQuery(`${country.name}`)
        setShowCountryDropdown(false)
    }

    const selectCryptoType = (cryptoType: CryptoType) => {
        setFormData(prev => ({
            ...prev,
            merchant_payout_crypto_type_id: cryptoType.id,
        }))
        setCryptoTypeSearchQuery(`${cryptoType.code} - ${cryptoType.name}`)
        setShowCryptoTypeDropdown(false)
    }

    const selectUser = (user: UserOption) => {
        setFormData(prev => ({
            ...prev,
            merchant_id: user.id,
        }))
        setUserSearchQuery(`${user.full_name} (${user.email})`)
        setShowUserDropdown(false)
    }

    const handleInputChange = (
        field: keyof AdminCreateUserData,
        value: string | number | boolean
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }))

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: '',
            }))
        }
    }

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {}

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required'
        }

        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required'
        }

        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required'
        }

        if (formData.create_merchant) {
            if (!formData.merchant_name?.trim()) {
                newErrors.merchant_name = 'Merchant name is required when creating merchant'
            }

            if (!formData.merchant_email?.trim()) {
                newErrors.merchant_email = 'Merchant email is required when creating merchant'
            }

            if (!formData.merchant_currency_id) {
                newErrors.merchant_currency_id = 'Merchant currency is required when creating merchant'
            }

            // Validate payout configuration
            if (formData.merchant_payout_crypto_enabled) {
                if (!formData.merchant_payout_wallet_address?.trim()) {
                    newErrors.merchant_payout_wallet_address = 'Payout wallet address is required when crypto payouts are enabled'
                }
                if (!formData.merchant_payout_crypto_type_id) {
                    newErrors.merchant_payout_crypto_type_id = 'Payout crypto type is required when crypto payouts are enabled'
                }
            }
        } else {
            if (formData.role.includes('merchant') && !formData.merchant_id) {
                newErrors.merchant_id = 'Merchant ID is required for merchant roles'
            }

            if (formData.role.includes('submerchant') && !formData.submerchant_id) {
                newErrors.submerchant_id = 'Submerchant ID is required for submerchant roles'
            }
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        setLoading(true)
        try {
            // Create a copy of formData and remove merchant_id if create_merchant is true
            const payload = { ...formData }
            if (payload.create_merchant) {
                delete payload.merchant_id
            }

            const response = await adminService.createUser(payload)

            if (response.success) {
                // Log successful user creation
                logDataModification(user, 'create_user', 'user', response.user?.id, true, 'User created successfully', {
                    userEmail: formData.email,
                    userRole: formData.role,
                    companyName: formData.company_name,
                    createMerchant: formData.create_merchant
                })

                setCreatedUser(response.user)
                setTemporaryPassword(response.temporary_password)
                // Navigate to admin users list or show success message
                // navigate('/admin/users')
            } else {
                // Log failed user creation
                logDataModification(user, 'create_user', 'user', undefined, false, 'Failed to create user', {
                    userEmail: formData.email,
                    userRole: formData.role,
                    error: response.message
                })
            }
        } catch (error: any) {
            const errorMessage = adminService.handleError(error)
            setErrors({ general: errorMessage })
        } finally {
            setLoading(false)
        }
    }

    if (createdUser) {
        return (
            <div
                className="min-h-screen"
                style={pageStyles.container}
            >
                <div className="relative z-10 p-6">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center mb-6 space-x-4">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/admin/users')}
                                className="p-2 text-white rounded-lg hover:bg-gray-800"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <div>
                                <div className="flex items-center space-x-3 mb-2">
                                    <div
                                        className="p-2 rounded-lg"
                                        style={{
                                            backgroundColor: statusColors.success.backgroundColor
                                        }}
                                    >
                                        <CheckCircle
                                            className="w-6 h-6"
                                            style={{ color: statusColors.success.color }}
                                        />
                                    </div>
                                    <h1
                                        className="text-4xl font-bold"
                                        style={pageStyles.text}
                                    >
                                        User Created Successfully!
                                    </h1>
                                </div>
                                <p
                                    className="text-lg"
                                    style={pageStyles.textSecondary}
                                >
                                    User and merchant have been created
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Success Card */}
                    <div className="mx-auto max-w-4xl">
                        <div
                            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
                            style={cardStyles}
                        >
                            <div className="p-8 card-content">
                                <div className="text-center mb-8">
                                    <div
                                        className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                                        style={{
                                            backgroundColor: statusColors.success.backgroundColor
                                        }}
                                    >
                                        <CheckCircle
                                            className="w-8 h-8"
                                            style={{ color: statusColors.success.color }}
                                        />
                                    </div>
                                    <h2
                                        className="text-2xl font-bold mb-2"
                                        style={pageStyles.text}
                                    >
                                        User Created Successfully
                                    </h2>
                                    <p
                                        style={pageStyles.textSecondary}
                                    >
                                        The user has been created with a temporary password
                                    </p>
                                </div>

                                {/* User Information */}
                                <div
                                    className="rounded-xl p-6 mb-6"
                                    style={{
                                        backgroundColor: `${branding.surfaceColor}80`
                                    }}
                                >
                                    <h3
                                        className="text-lg font-semibold mb-4"
                                        style={pageStyles.text}
                                    >
                                        User Information
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label
                                                className="block text-sm font-medium"
                                                style={pageStyles.textSecondary}
                                            >
                                                Name
                                            </label>
                                            <p style={pageStyles.text}>{createdUser.first_name} {createdUser.last_name}</p>
                                        </div>
                                        <div>
                                            <label
                                                className="block text-sm font-medium"
                                                style={pageStyles.textSecondary}
                                            >
                                                Email
                                            </label>
                                            <p style={pageStyles.text}>{createdUser.email}</p>
                                        </div>
                                        <div>
                                            <label
                                                className="block text-sm font-medium"
                                                style={pageStyles.textSecondary}
                                            >
                                                Role
                                            </label>
                                            <p style={pageStyles.text}>{createdUser.role}</p>
                                        </div>
                                        <div>
                                            <label
                                                className="block text-sm font-medium"
                                                style={pageStyles.textSecondary}
                                            >
                                                Status
                                            </label>
                                            <p style={pageStyles.text}>{createdUser.is_active ? 'Active' : 'Inactive'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Temporary Password */}
                                <div
                                    className="border rounded-xl p-6 mb-6"
                                    style={{
                                        backgroundColor: `${branding.primaryColor}10`,
                                        borderColor: `${branding.primaryColor}30`
                                    }}
                                >
                                    <h3
                                        className="text-lg font-semibold mb-4"
                                        style={{ color: branding.primaryColor }}
                                    >
                                        Temporary Password
                                    </h3>
                                    <div className="flex items-center space-x-4">
                                        <div className="flex-1">
                                            <input
                                                type={showTemporaryPassword ? 'text' : 'password'}
                                                value={temporaryPassword}
                                                readOnly
                                                className="w-full px-4 py-3 rounded-xl font-mono text-lg"
                                                style={{
                                                    backgroundColor: `${branding.surfaceColor}80`,
                                                    borderColor: `${branding.textSecondaryColor}30`,
                                                    color: branding.textColor,
                                                }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowTemporaryPassword(!showTemporaryPassword)}
                                            className="p-3 rounded-xl transition-colors"
                                            style={{
                                                backgroundColor: `${branding.surfaceColor}80`,
                                                color: branding.textSecondaryColor
                                            }}
                                        >
                                            {showTemporaryPassword ? (
                                                <EyeOff className="w-5 h-5" />
                                            ) : (
                                                <Eye className="w-5 h-5" />
                                            )}
                                        </button>
                                    </div>
                                    <p
                                        className="text-sm mt-2"
                                        style={{ color: branding.primaryColor }}
                                    >
                                        ⚠️ Please share this password with the user securely. They should change it on first login.
                                    </p>
                                </div>

                                {/* Actions */}
                                <div className="flex flex-col items-center justify-end space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate('/admin/users')}
                                        className="w-full px-8 py-3 sm:w-auto"
                                        style={{
                                            backgroundColor: 'transparent',
                                            borderColor: branding.textSecondaryColor,
                                            color: branding.textSecondaryColor
                                        }}
                                    >
                                        View All Users
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            setCreatedUser(null)
                                            setTemporaryPassword('')
                                            setFormData({
                                                email: '',
                                                first_name: '',
                                                last_name: '',
                                                role: canManageUsers(user) ? 'merchant_admin' : 'merchant_user',
                                                company_name: '',
                                                phone_number: '',
                                                country: undefined,
                                                merchant_id: undefined,
                                                submerchant_id: undefined,
                                                send_welcome_email: true,
                                                create_merchant: false,
                                                merchant_name: '',
                                                merchant_email: '',
                                                merchant_description: '',
                                                merchant_website_url: '',
                                                merchant_currency_id: undefined,
                                                merchant_country_id: undefined,
                                                merchant_success_redirect_url: '',
                                                merchant_cancel_redirect_url: '',
                                                merchant_webhook_url: '',
                                                merchant_commission_rate: 0.00,
                                                merchant_payout_crypto_enabled: false,
                                                merchant_payout_wallet_address: '',
                                                merchant_payout_crypto_type_id: undefined,
                                            })
                                        }}
                                        className="w-full px-8 py-3 sm:w-auto"
                                        style={{
                                            backgroundColor: branding.primaryColor,
                                            color: branding.textColor
                                        }}
                                    >
                                        <Plus className="w-5 h-5 mr-2" />
                                        Create Another User
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className="min-h-screen"
            style={pageStyles.container}
        >
            <div className="relative z-10 p-6">

                {/* Form */}
                <div className="mx-auto">
                    <div
                        className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
                        style={cardStyles}
                    >
                        <div className="p-8 card-content">
                            <form onSubmit={handleSubmit} className="space-y-8">
                                {/* General Error */}
                                {errors.general && (
                                    <div
                                        className="flex items-center p-4 space-x-3 border rounded-lg"
                                        style={{
                                            backgroundColor: statusColors.error.backgroundColor,
                                            borderColor: statusColors.error.borderColor
                                        }}
                                    >
                                        <AlertCircle
                                            className="flex-shrink-0 w-5 h-5"
                                            style={{ color: statusColors.error.color }}
                                        />
                                        <p style={{ color: statusColors.error.color }}>{errors.general}</p>
                                    </div>
                                )}

                                {/* User Information */}
                                <div className="space-y-6">
                                    <div className="pb-4">
                                        <h2
                                            className="flex items-center space-x-3 text-2xl font-semibold"
                                            style={pageStyles.text}
                                        >
                                            <div
                                                className="p-2 rounded-lg"
                                                style={{
                                                    backgroundColor: `${branding.primaryColor}20`
                                                }}
                                            >
                                                <User
                                                    className="w-6 h-6"
                                                    style={{ color: branding.primaryColor }}
                                                />
                                            </div>
                                            <span>User Information</span>
                                        </h2>
                                        <p
                                            className="mt-2"
                                            style={pageStyles.textSecondary}
                                        >
                                            Enter the basic user information
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                        <div>
                                            <label
                                                className="block mb-3 text-sm font-semibold"
                                                style={pageStyles.textSecondary}
                                            >
                                                First Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.first_name}
                                                onChange={e => handleInputChange('first_name', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.first_name
                                                    ? 'border-orange-500 focus:ring-orange-500'
                                                    : 'hover:border-gray-500'
                                                    }`}
                                                style={{
                                                    backgroundColor: `${branding.surfaceColor}80`,
                                                    borderColor: errors.first_name ? statusColors.error.borderColor : `${branding.textSecondaryColor}30`,
                                                    color: branding.textColor,
                                                }}
                                                placeholder="John"
                                            />
                                            {errors.first_name && (
                                                <p
                                                    className="flex items-center mt-2 space-x-1 text-sm"
                                                    style={{ color: statusColors.error.color }}
                                                >
                                                    <XCircle className="w-4 h-4" />
                                                    <span>{errors.first_name}</span>
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block mb-3 text-sm font-semibold" style={pageStyles.textSecondary}>
                                                Last Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.last_name}
                                                onChange={e => handleInputChange('last_name', e.target.value)}
                                                className={`w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.last_name
                                                    ? 'border-orange-500 focus:ring-orange-500'
                                                    : 'hover:border-gray-500'
                                                    }`}
                                                placeholder="Doe"
                                            />
                                            {errors.last_name && (
                                                <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                                                    <XCircle className="w-4 h-4" />
                                                    <span>{errors.last_name}</span>
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                Email *
                                            </label>
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={e => handleInputChange('email', e.target.value)}
                                                className={`w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.email
                                                    ? 'border-orange-500 focus:ring-orange-500'
                                                    : 'hover:border-gray-500'
                                                    }`}
                                                placeholder="john@example.com"
                                            />
                                            {errors.email && (
                                                <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                                                    <XCircle className="w-4 h-4" />
                                                    <span>{errors.email}</span>
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                Role *
                                            </label>
                                            <select
                                                value={formData.role}
                                                onChange={e => handleInputChange('role', e.target.value as any)}
                                                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                            >
                                                {getVisibleRoles(user).map(role => (
                                                    <option key={role} value={role}>
                                                        {role === 'admin' ? 'Platform Admin' :
                                                            role === 'merchant_admin' ? 'Merchant Admin' :
                                                                role === 'merchant_user' ? 'Merchant User' :
                                                                    role === 'submerchant_admin' ? 'Submerchant Admin' :
                                                                        role === 'submerchant_user' ? 'Submerchant User' :
                                                                            role === 'auditor' ? 'Auditor' : role}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                Company Name
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.company_name}
                                                onChange={e => handleInputChange('company_name', e.target.value)}
                                                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                placeholder="Acme Corp"
                                            />
                                        </div>

                                        <div>
                                            <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                Phone Number
                                            </label>
                                            <input
                                                type="tel"
                                                value={formData.phone_number}
                                                onChange={e => handleInputChange('phone_number', e.target.value)}
                                                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                placeholder="+1234567890"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Merchant Creation */}
                                <div className="space-y-6">
                                    <div className="pb-4 border-b">
                                        <h2
                                            className="flex items-center space-x-3 text-2xl font-semibold"
                                            style={pageStyles.text}
                                        >
                                            <div
                                                className="p-2 rounded-lg"
                                                style={{
                                                    backgroundColor: `${branding.primaryColor}20`
                                                }}
                                            >
                                                <Building
                                                    className="w-6 h-6"
                                                    style={{ color: branding.primaryColor }}
                                                />
                                            </div>
                                            <span>Merchant Creation</span>
                                        </h2>
                                        <p
                                            className="mt-2"
                                            style={pageStyles.textSecondary}
                                        >
                                            Choose whether to create a new merchant or assign to existing one
                                        </p>
                                    </div>

                                    <div className="flex items-center space-x-4">
                                        <label className="flex items-center space-x-3 cursor-pointer group">
                                            <div className="relative">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.create_merchant}
                                                    onChange={e => handleInputChange('create_merchant', e.target.checked)}
                                                    className="sr-only"
                                                />
                                                <div
                                                    className={`w-5 h-5 border-2 rounded-md transition-all duration-200 flex items-center justify-center ${formData.create_merchant
                                                        ? 'shadow-lg'
                                                        : 'group-hover:border-orange-400 group-hover:bg-gray-600/50'
                                                        }`}
                                                    style={{
                                                        backgroundColor: formData.create_merchant ? branding.primaryColor : `${branding.surfaceColor}80`,
                                                        borderColor: formData.create_merchant ? branding.primaryColor : branding.textSecondaryColor,
                                                        boxShadow: formData.create_merchant ? `0 10px 15px -3px ${branding.primaryColor}25` : 'none'
                                                    }}
                                                >
                                                    {formData.create_merchant && (
                                                        <CheckCircle
                                                            className="w-3 h-3"
                                                            style={{ color: branding.textColor }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col">
                                                <span
                                                    className="text-sm font-semibold transition-colors"
                                                    style={{ color: branding.textSecondaryColor }}
                                                >
                                                    Create New Merchant
                                                </span>
                                                <span
                                                    className="text-xs"
                                                    style={{ color: branding.textSecondaryColor }}
                                                >
                                                    {formData.create_merchant ? 'Will create a new merchant' : 'Will assign to existing merchant'}
                                                </span>
                                            </div>
                                        </label>
                                    </div>

                                    {formData.create_merchant ? (
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                                <div>
                                                    <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                        Merchant Name *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={formData.merchant_name}
                                                        onChange={e => handleInputChange('merchant_name', e.target.value)}
                                                        className={`w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.merchant_name
                                                            ? 'border-orange-500 focus:ring-orange-500'
                                                            : 'hover:border-gray-500'
                                                            }`}
                                                        placeholder="Acme Corp"
                                                    />
                                                    {errors.merchant_name && (
                                                        <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                                                            <XCircle className="w-4 h-4" />
                                                            <span>{errors.merchant_name}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                        Merchant Email *
                                                    </label>
                                                    <input
                                                        type="email"
                                                        value={formData.merchant_email}
                                                        onChange={e => handleInputChange('merchant_email', e.target.value)}
                                                        className={`w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.merchant_email
                                                            ? 'border-orange-500 focus:ring-orange-500'
                                                            : 'hover:border-gray-500'
                                                            }`}
                                                        placeholder="contact@acmecorp.com"
                                                    />
                                                    {errors.merchant_email && (
                                                        <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                                                            <XCircle className="w-4 h-4" />
                                                            <span>{errors.merchant_email}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="md:col-span-2">
                                                    <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                        Merchant Description
                                                    </label>
                                                    <textarea
                                                        value={formData.merchant_description}
                                                        onChange={e => handleInputChange('merchant_description', e.target.value)}
                                                        rows={3}
                                                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                        placeholder="Describe the merchant's business"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                        Currency *
                                                    </label>
                                                    <div className="relative" ref={currencyDropdownRef}>
                                                        <div className="relative">
                                                            <Search className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
                                                            <input
                                                                type="text"
                                                                value={currencySearchQuery}
                                                                onChange={e => handleCurrencySearch(e.target.value)}
                                                                onFocus={() => setShowCurrencyDropdown(true)}
                                                                placeholder="Search currencies (e.g., USD, Euro, Dollar)"
                                                                className="w-full px-4 py-3 pl-12 pr-12 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                                                                className="absolute text-gray-400 transform -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                                                            >
                                                                <ChevronDown
                                                                    className={`h-5 w-5 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`}
                                                                />
                                                            </button>
                                                        </div>

                                                        {showCurrencyDropdown && (
                                                            <div className="absolute z-50 w-full mt-1 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg max-h-64">
                                                                <div className="overflow-y-auto max-h-64">
                                                                    {currencyLoading ? (
                                                                        <div className="p-4 text-center">
                                                                            <Loader2 className="w-5 h-5 mx-auto text-gray-400 animate-spin" />
                                                                            <p className="mt-2 text-sm text-gray-500">Searching currencies...</p>
                                                                        </div>
                                                                    ) : currencies.length > 0 ? (
                                                                        currencies.map(currency => (
                                                                            <button
                                                                                key={currency.id}
                                                                                type="button"
                                                                                onClick={() => selectCurrency(currency)}
                                                                                className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50"
                                                                            >
                                                                                <div>
                                                                                    <div className="font-medium text-gray-900">
                                                                                        {currency.code} - {currency.name}
                                                                                    </div>
                                                                                    <div className="text-sm text-gray-500">
                                                                                        {currency.symbol} • {currency.decimal_places} decimal places
                                                                                    </div>
                                                                                </div>
                                                                            </button>
                                                                        ))
                                                                    ) : currencySearchQuery.length > 0 ? (
                                                                        <div className="p-4 text-center text-gray-500">
                                                                            <p className="text-sm">
                                                                                No currencies found for "{currencySearchQuery}"
                                                                            </p>
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {errors.merchant_currency_id && (
                                                        <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                                                            <XCircle className="w-4 h-4" />
                                                            <span>{errors.merchant_currency_id}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                        Country
                                                    </label>
                                                    <div className="relative" ref={countryDropdownRef}>
                                                        <div className="relative">
                                                            <Search className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
                                                            <input
                                                                type="text"
                                                                value={countrySearchQuery}
                                                                onChange={e => handleCountrySearch(e.target.value)}
                                                                onFocus={() => setShowCountryDropdown(true)}
                                                                placeholder="Search countries"
                                                                className="w-full px-4 py-3 pl-12 pr-12 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                                                className="absolute text-gray-400 transform -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                                                            >
                                                                <ChevronDown
                                                                    className={`h-5 w-5 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`}
                                                                />
                                                            </button>
                                                        </div>

                                                        {showCountryDropdown && (
                                                            <div className="absolute z-50 w-full mt-1 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg max-h-64">
                                                                <div className="overflow-y-auto max-h-64">
                                                                    {countryLoading ? (
                                                                        <div className="p-4 text-center">
                                                                            <Loader2 className="w-5 h-5 mx-auto text-gray-400 animate-spin" />
                                                                            <p className="mt-2 text-sm text-gray-500">Searching countries...</p>
                                                                        </div>
                                                                    ) : countries.length > 0 ? (
                                                                        countries.map(country => (
                                                                            <button
                                                                                key={country.id}
                                                                                type="button"
                                                                                onClick={() => selectCountry(country)}
                                                                                className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50"
                                                                            >
                                                                                <div>
                                                                                    <div className="font-medium text-gray-900">
                                                                                        {country.name}
                                                                                    </div>
                                                                                    <div className="text-sm text-gray-500">
                                                                                        {country.country_code}
                                                                                    </div>
                                                                                </div>
                                                                            </button>
                                                                        ))
                                                                    ) : countrySearchQuery.length > 0 ? (
                                                                        <div className="p-4 text-center text-gray-500">
                                                                            <p className="text-sm">
                                                                                No countries found for "{countrySearchQuery}"
                                                                            </p>
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                        Website URL
                                                    </label>
                                                    <div className="relative">
                                                        <Globe className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
                                                        <input
                                                            type="url"
                                                            value={formData.merchant_website_url}
                                                            onChange={e => handleInputChange('merchant_website_url', e.target.value)}
                                                            className="w-full px-4 py-3 pl-12 pr-4 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                            placeholder="https://acmecorp.com"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                        Commission Rate (%)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        max="100"
                                                        value={formData.merchant_commission_rate}
                                                        onChange={e => handleInputChange('merchant_commission_rate', parseFloat(e.target.value) || 0)}
                                                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                        placeholder="2.50"
                                                    />
                                                </div>
                                            </div>

                                            {/* Payout Configuration */}
                                            <div className="space-y-6">
                                                <div className="pb-4 border-b border-gray-700">
                                                    <h3 className="flex items-center space-x-3 text-xl font-semibold text-white" style={{
                                                        color: formData.merchant_payout_crypto_enabled ? branding.textColor : branding.textSecondaryColor
                                                    }}>
                                                        <div className="p-2 rounded-lg bg-green-500/20" style={{
                                                            backgroundColor: formData.merchant_payout_crypto_enabled ? branding.primaryColor : `${branding.surfaceColor}80`
                                                        }}>
                                                            <Building className="w-5 h-5 text-green-400" style={{
                                                                color: formData.merchant_payout_crypto_enabled ? branding.textColor : branding.textSecondaryColor
                                                            }} />
                                                        </div>
                                                        <span style={{
                                                            color: formData.merchant_payout_crypto_enabled ? branding.textColor : branding.textSecondaryColor
                                                        }}>Crypto Payout Configuration</span>
                                                    </h3>
                                                    <p className="mt-2 text-gray-300" style={{
                                                        color: formData.merchant_payout_crypto_enabled ? branding.textColor : branding.textSecondaryColor
                                                    }}>
                                                        Configure cryptocurrency payout settings for this merchant
                                                    </p>
                                                </div>

                                                <div className="flex items-center space-x-4">
                                                    <label className="flex items-center space-x-3 cursor-pointer group">
                                                        <div className="relative">
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.merchant_payout_crypto_enabled}
                                                                onChange={e => handleInputChange('merchant_payout_crypto_enabled', e.target.checked)}
                                                                className="sr-only"
                                                            />
                                                            <div
                                                                className={`w-5 h-5 border-2 rounded-md transition-all duration-200 flex items-center justify-center ${formData.merchant_payout_crypto_enabled
                                                                    ? 'bg-green-500 border-green-500 shadow-lg shadow-green-500/25'
                                                                    : 'border-gray-500 bg-gray-700/50 group-hover:border-green-400 group-hover:bg-gray-600/50'
                                                                    }`}
                                                                style={{
                                                                    backgroundColor: formData.merchant_payout_crypto_enabled ? branding.primaryColor : `${branding.surfaceColor}80`,
                                                                    borderColor: formData.merchant_payout_crypto_enabled ? branding.primaryColor : branding.textSecondaryColor,
                                                                    boxShadow: formData.merchant_payout_crypto_enabled ? `0 10px 15px -3px ${branding.primaryColor}25` : 'none'
                                                                }}
                                                            >
                                                                {formData.merchant_payout_crypto_enabled && <CheckCircle className="w-3 h-3 text-white" />}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-semibold text-gray-300 group-hover:text-white transition-colors" style={{
                                                                color: formData.merchant_payout_crypto_enabled ? branding.textColor : branding.textSecondaryColor
                                                            }}>Enable Crypto Payouts</span>
                                                            <span className="text-xs text-gray-400" style={{
                                                                color: formData.merchant_payout_crypto_enabled ? branding.textColor : branding.textSecondaryColor
                                                            }}>
                                                                {formData.merchant_payout_crypto_enabled ? 'Crypto payouts enabled' : 'Crypto payouts disabled'}
                                                            </span>
                                                        </div>
                                                    </label>
                                                </div>

                                                {formData.merchant_payout_crypto_enabled && (
                                                    <div className="space-y-6">
                                                        <div>
                                                            <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                                Crypto Type *
                                                            </label>
                                                            <div className="relative" ref={cryptoTypeDropdownRef}>
                                                                <div className="relative">
                                                                    <Search className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
                                                                    <input
                                                                        type="text"
                                                                        value={cryptoTypeSearchQuery}
                                                                        onChange={e => handleCryptoTypeSearch(e.target.value)}
                                                                        onFocus={() => setShowCryptoTypeDropdown(true)}
                                                                        placeholder="Search crypto types (e.g., BTC, ETH, USDT)"
                                                                        className="w-full px-4 py-3 pl-12 pr-12 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowCryptoTypeDropdown(!showCryptoTypeDropdown)}
                                                                        className="absolute text-gray-400 transform -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                                                                    >
                                                                        <ChevronDown
                                                                            className={`h-5 w-5 transition-transform ${showCryptoTypeDropdown ? 'rotate-180' : ''}`}
                                                                        />
                                                                    </button>
                                                                </div>

                                                                {showCryptoTypeDropdown && (
                                                                    <div className="absolute z-50 w-full mt-1 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg max-h-64">
                                                                        <div className="overflow-y-auto max-h-64">
                                                                            {cryptoTypeLoading ? (
                                                                                <div className="p-4 text-center">
                                                                                    <Loader2 className="w-5 h-5 mx-auto text-gray-400 animate-spin" />
                                                                                    <p className="mt-2 text-sm text-gray-500">Searching crypto types...</p>
                                                                                </div>
                                                                            ) : cryptoTypes.length > 0 ? (
                                                                                cryptoTypes.map(cryptoType => (
                                                                                    <button
                                                                                        key={cryptoType.id}
                                                                                        type="button"
                                                                                        onClick={() => selectCryptoType(cryptoType)}
                                                                                        className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50"
                                                                                    >
                                                                                        <div>
                                                                                            <div className="font-medium text-gray-900">
                                                                                                {cryptoType.code} - {cryptoType.name}
                                                                                            </div>
                                                                                            <div className="text-sm text-gray-500">
                                                                                                Min payout: {cryptoType.min_payout}
                                                                                            </div>
                                                                                        </div>
                                                                                    </button>
                                                                                ))
                                                                            ) : cryptoTypeSearchQuery.length > 0 ? (
                                                                                <div className="p-4 text-center text-gray-500">
                                                                                    <p className="text-sm">
                                                                                        No crypto types found for "{cryptoTypeSearchQuery}"
                                                                                    </p>
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {errors.merchant_payout_crypto_type_id && (
                                                                <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                                                                    <XCircle className="w-4 h-4" />
                                                                    <span>{errors.merchant_payout_crypto_type_id}</span>
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                                Wallet Address *
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={formData.merchant_payout_wallet_address}
                                                                onChange={e => handleInputChange('merchant_payout_wallet_address', e.target.value)}
                                                                className={`w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.merchant_payout_wallet_address
                                                                    ? 'border-orange-500 focus:ring-orange-500'
                                                                    : 'hover:border-gray-500'
                                                                    }`}
                                                                placeholder="Enter cryptocurrency wallet address"
                                                            />
                                                            {errors.merchant_payout_wallet_address && (
                                                                <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                                                                    <XCircle className="w-4 h-4" />
                                                                    <span>{errors.merchant_payout_wallet_address}</span>
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="block mb-3 text-sm font-semibold text-gray-300">
                                                Select Existing Merchant
                                            </label>
                                            <div className="relative" ref={userDropdownRef}>
                                                <div className="relative">
                                                    <Search className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
                                                    <input
                                                        type="text"
                                                        value={userSearchQuery}
                                                        onChange={e => handleUserSearch(e.target.value)}
                                                        onFocus={() => setShowUserDropdown(true)}
                                                        placeholder="Search merchants by name or email"
                                                        className="w-full px-4 py-3 pl-12 pr-12 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowUserDropdown(!showUserDropdown)}
                                                        className="absolute text-gray-400 transform -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                                                    >
                                                        <ChevronDown
                                                            className={`h-5 w-5 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`}
                                                        />
                                                    </button>
                                                </div>

                                                {showUserDropdown && (
                                                    <div className="absolute z-50 w-full mt-1 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg max-h-64">
                                                        <div className="overflow-y-auto max-h-64">
                                                            {userLoading ? (
                                                                <div className="p-4 text-center">
                                                                    <Loader2 className="w-5 h-5 mx-auto text-gray-400 animate-spin" />
                                                                    <p className="mt-2 text-sm text-gray-500">Searching merchants...</p>
                                                                </div>
                                                            ) : users.length > 0 ? (
                                                                users.map(user => (
                                                                    <button
                                                                        key={user.id}
                                                                        type="button"
                                                                        onClick={() => selectUser(user)}
                                                                        className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50"
                                                                    >
                                                                        <div className="flex items-center space-x-3">
                                                                            <div className="p-1 bg-gray-100 rounded-full">
                                                                                <User className="w-4 h-4 text-gray-600" />
                                                                            </div>
                                                                            <div>
                                                                                <div className="font-medium text-gray-900">
                                                                                    {user.full_name}
                                                                                </div>
                                                                                <div className="text-sm text-gray-500">
                                                                                    {user.email} • {user.role}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </button>
                                                                ))
                                                            ) : userSearchQuery.length > 0 ? (
                                                                <div className="p-4 text-center text-gray-500">
                                                                    <p className="text-sm">No merchants found for "{userSearchQuery}"</p>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {errors.merchant_id && (
                                                <p className="flex items-center mt-2 space-x-1 text-sm text-orange-600">
                                                    <XCircle className="w-4 h-4" />
                                                    <span>{errors.merchant_id}</span>
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Email Settings */}
                                <div className="space-y-6">
                                    <div className="pb-4 border-b" style={{ borderBottomColor: `${branding.textColor}30` }}>
                                        <h2
                                            className="flex items-center space-x-3 text-2xl font-semibold"
                                            style={pageStyles.text}
                                        >
                                            <div
                                                className="p-2 rounded-lg"
                                                style={{
                                                    backgroundColor: `${branding.secondaryColor}20`
                                                }}
                                            >
                                                <Mail
                                                    className="w-6 h-6"
                                                    style={{ color: branding.primaryColor }}

                                                />
                                            </div>
                                            <span>Email Settings</span>
                                        </h2>
                                        <p
                                            className="mt-2"
                                            style={pageStyles.textSecondary}
                                        >
                                            Configure email notifications
                                        </p>
                                    </div>

                                    <div className="flex items-center space-x-4">
                                        <label className="flex items-center space-x-3 cursor-pointer group">
                                            <div className="relative">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.send_welcome_email}
                                                    style={{
                                                        backgroundColor: formData.send_welcome_email ? branding.primaryColor : `${branding.surfaceColor}80`,
                                                        borderColor: formData.send_welcome_email ? branding.primaryColor : branding.textSecondaryColor,
                                                        color: branding.textColor
                                                    }}
                                                    onChange={e => handleInputChange('send_welcome_email', e.target.checked)}
                                                    className="sr-only"
                                                />
                                                <div
                                                    className={`w-5 h-5 border-2 rounded-md transition-all duration-200 flex items-center justify-center ${formData.send_welcome_email
                                                        ? 'bg-orange-500 border-orange-500 shadow-lg shadow-orange-500/25'
                                                        : 'border-gray-500 bg-gray-700/50 group-hover:border-orange-400 group-hover:bg-gray-600/50'
                                                        }`}
                                                    style={{
                                                        backgroundColor: formData.send_welcome_email ? branding.primaryColor : `${branding.surfaceColor}80`,
                                                        borderColor: formData.send_welcome_email ? branding.primaryColor : branding.textSecondaryColor,
                                                        color: branding.textColor
                                                    }}
                                                >
                                                    {formData.send_welcome_email && <CheckCircle className="w-3 h-3 text-white" />}
                                                </div>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-semibold"
                                                    style={{
                                                        color: formData.send_welcome_email ? branding.textColor : branding.textSecondaryColor
                                                    }}
                                                >
                                                    Send Welcome Email
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    {formData.send_welcome_email ? 'Will send welcome email with temporary password' : 'Will not send welcome email'}
                                                </span>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                {/* Form Actions */}
                                <div
                                    className="flex flex-col items-center justify-end pt-8 space-y-4 border-t sm:flex-row sm:space-y-0 sm:space-x-4"
                                    style={{ borderTopColor: `${branding.textSecondaryColor}30` }}
                                >
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate('/admin/users')}
                                        className="w-full px-8 py-3 sm:w-auto"
                                        style={{
                                            backgroundColor: 'transparent',
                                            borderColor: branding.textSecondaryColor,
                                            color: branding.textSecondaryColor
                                        }}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="w-full px-8 py-3 sm:w-auto"
                                        style={{
                                            backgroundColor: branding.primaryColor,
                                            color: branding.textColor
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                Creating User...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="w-5 h-5 mr-2" />
                                                Create User
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
